import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { safeLocalStorageSet } from "@/utils/safe-local-storage";

// Calm Authority is light-only. The theme is fixed to "light"; this provider
// guarantees the document always resolves to light (never .dark), regardless of
// OS preference or any stale stored value, and keeps a stable useTheme()
// contract for consumers.
export type Theme = "light";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: "light";
  setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = "vardic_theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);
export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Force light: drop any stray .dark, pin data-theme, normalize storage.
    document.documentElement.classList.remove("dark");
    document.documentElement.dataset.theme = "light";
    safeLocalStorageSet(STORAGE_KEY, "light");
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: "light",
      resolvedTheme: "light",
      // Light-only: theme switching is intentionally a no-op.
      setTheme: () => {},
    }),
    [],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);

  if (!value) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return value;
}
